"use client";

import { createContext, useContext, type ReactNode } from "react";

import type { ProblemHistory } from "../types/problem";

/**
 * 공용 ProblemAnswer 안에 앱이 끼워 넣는 UI 조각.
 *
 * 광고·추천·회차 이력 패널은 앱마다 상품과 백엔드가 다르다. 공용 패키지가 이들을 직접
 * import 하면 빌드하는 앱과 무관하게 특정 앱의 광고 SDK·검색 인덱스·서버 액션을 물게 되므로
 * 렌더 함수로 주입받는다. 넘기지 않으면 해당 영역을 렌더하지 않는다.
 */
/** 문제 유형 뱃지. 앱의 ProblemHistory 표시 규칙과 같은 리터럴 집합이다. */
export type ProblemSlotType = "기초" | "비추천" | "기출변형" | "기출";

export interface ProblemHistorySlotProps {
  isSignedIn: boolean;
  /** Clerk 사용자. 이력 패널이 소유자 판별에만 쓰므로 id 만 계약에 둔다. */
  user: { id: string } | null | undefined;
  id?: string;
  isLoadingHistory: boolean;
  problemHistory: ProblemHistory | null;
  isCorrectAnswerVisible: boolean;
  isCorrect: boolean;
  hasPremiumAccess: boolean;
  problemTypes?: ProblemSlotType[];
  premium?: boolean;
  variationNote?: string;
  showCurrentFeedback: boolean;
}

export interface ProblemAnswerSlots {
  /** 풀이 이력 패널(회차별 시도·오답노트). JCG 는 PreviousSessionDialog 까지 포함한다. */
  renderHistoryPanel?: (props: ProblemHistorySlotProps) => ReactNode;
  /** 정답 공개 시 노출하는 전면 광고. */
  renderSolutionAd?: (props: { open: boolean; onClose: () => void }) => ReactNode;
  /** 추천 문제 영역. */
  renderRecommendations?: (props: { problemId: string }) => ReactNode;
}

const ProblemAnswerSlotsContext = createContext<ProblemAnswerSlots>({});

export function ProblemAnswerSlotsProvider({
  slots,
  children,
}: {
  slots: ProblemAnswerSlots;
  children: ReactNode;
}) {
  return (
    <ProblemAnswerSlotsContext.Provider value={slots}>
      {children}
    </ProblemAnswerSlotsContext.Provider>
  );
}

export function useProblemAnswerSlots(): ProblemAnswerSlots {
  return useContext(ProblemAnswerSlotsContext);
}
